// Peppol integration types

// Peppol environment types
export type PeppolEnvironment = 'test' | 'production' | 'TEST' | 'PRODUCTION';

// Peppol configuration for a firm
export interface PeppolConfig {
  id: number;
  firm: number;
  environment: PeppolEnvironment;
  is_enabled: boolean;
  is_configured: boolean;
  default_participant: number | null;
  default_participant_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface PeppolConfigUpdate {
  is_enabled?: boolean;
  default_participant_id?: number | null;
}

// Peppol sender participant (available sender identities for the firm)
export interface PeppolSenderParticipant {
  id: number;
  name: string;
  participant_id: string;
  participant_scheme: string;
  peppol_identifier: string;
  is_active: boolean;
  is_registered: boolean;
}

export interface PeppolConnectionTest {
  success: boolean;
  message: string;
  environment: PeppolEnvironment;
  token_valid: boolean;
}

// Peppol participant lookup (for verifying recipients on the network)
export interface PeppolParticipantLookup {
  participant_id: string;
  scheme: string;
}

export interface PeppolParticipantLookupResult {
  exists: boolean;
  participant_id: string;
  scheme: string;
  country_code: string;
  document_types: {
    identifier: string;
    scheme: string;
  }[];
}

// Peppol document types
export type PeppolDocumentDirection = 'outgoing' | 'incoming';
export type PeppolDocumentStatus = 
  | 'pending'
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'acknowledged'
  | 'rejected'
  | 'failed'
  | 'received'
  | 'processed';

export interface PeppolDocument {
  id: number;
  invoice: number | null;
  document_type: 'invoice' | 'credit_note' | 'downpayment' | null;
  direction: PeppolDocumentDirection;
  status: PeppolDocumentStatus;
  sender_id: string;
  sender_scheme: string;
  recipient_id: string;
  recipient_scheme: string;
  // API also returns these combined fields (scheme:id)
  sender?: string | null;
  recipient?: string | null;
  netfly_document_id: string | null;
  response_code: string | null;
  response_message: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  created_at: string;
  updated_at?: string;
}

export interface PeppolDocumentFilters {
  direction?: PeppolDocumentDirection;
  status?: PeppolDocumentStatus;
  invoice?: number;
  created_after?: string;
  created_before?: string;
}

export interface PeppolDocumentSync {
  direction?: 'outgoing' | 'incoming' | 'all';
  days?: number;
}

export interface PeppolDocumentSyncResult {
  synced: number;
  new_documents: number;
  updated_documents: number;
  errors: string[];
}

export interface PeppolDocumentValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

// Invoice Peppol status
export interface InvoicePeppolStatus {
  peppol_enabled: boolean;
  client_peppol_enabled: boolean;
  has_been_sent: boolean;
  document_id: number | null;
  status: PeppolDocumentStatus | null;
  sent_at: string | null;
  delivered_at: string | null;
  response_code: string | null;
  response_message: string | null;
}

export interface InvoicePeppolValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface InvoicePeppolSendRequest {
  validate_first?: boolean;
}

export interface InvoicePeppolSendResult {
  success: boolean;
  document_id?: number;
  status?: PeppolDocumentStatus;
  message?: string;
  error?: string;
  validation_errors?: string[];
}

export interface InvoicePeppolPreview {
  xml: string;
}

// Peppol webhook configuration
export interface PeppolWebhook {
  id: number;
  webhook_url: string;
  is_active: boolean;
  secret: string;
  created_at: string;
  updated_at?: string;
}

export interface PeppolWebhookCreate {
  webhook_url: string;
}

// Peppol-enabled client types
export type PeppolClientLookupStatus = 'verified' | 'not_verified' | 'not_found';

export interface PeppolClient {
  id: number;
  name: string;
  peppol_id: string | null;
  peppol_scheme: string | null;
  scheme_display: string | null;
  peppol_identifier: string | null;
  email: string | null;
  country: string | null;
  lookup_status: PeppolClientLookupStatus;
  last_lookup: string | null;
}

export interface PeppolClientLookupResult {
  exists: boolean;
  participant_id: string;
  participant_scheme: string;
  document_types: {
    identifier: string;
    scheme: string;
  }[];
  smp_host_uri?: string;
}

export interface PeppolClientLookupAllResult {
  verified: number;
  not_found: number;
  failed: number;
  details: {
    client_id: number;
    client_name: string;
    peppol_id: string;
    status: 'verified' | 'not_found' | 'failed';
  }[];
}

// Sender identity types
export interface PeppolSenderIdentity {
  id: number;
  name: string;
  participant_id: string;
  participant_scheme: string;
  peppol_identifier: string;
  is_active: boolean;
  is_registered: boolean;
}

export interface PeppolCurrentSender {
  configured: boolean;
  sender: PeppolSenderIdentity | null;
}

// Firm defaults for self-service sender creation
export interface PeppolFirmDefaults {
  name: string;
  vat_number: string;
  has_vat_number: boolean;
  email: string;
  phone: string;
  website: string;
  country: string;
  address: string;
  suggested_scheme: string;
  user_name: string;
  user_email: string;
}

// Contact type options for sender creation
export type PeppolContactType = 'CEO' | 'CFO' | 'ADMIN' | 'TECH' | 'OTHER';

// Request for creating a sender from firm data
export interface PeppolCreateSenderRequest {
  // Required address fields
  street: string;
  street_number: string;
  zip_code: string;
  city: string;
  country: string;
  // Optional overrides
  participant_id?: string;
  participant_scheme?: string;
  name?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_type?: PeppolContactType;
  web_uri?: string;
  set_as_default?: boolean;
}

// Response from creating a sender
export interface PeppolCreatedSender {
  id: number;
  firm: number;
  name: string;
  participant_id: string;
  participant_scheme: string;
  peppol_identifier: string;
  is_active: boolean;
  is_registered: boolean;
  street: string;
  street_number: string;
  zip_code: string;
  city: string;
  country: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
}

// Common Peppol participant schemes
export const PEPPOL_SCHEMES = {
  '0208': { code: '0208', name: 'Belgium VAT', example: 'BE0123456789' },
  '0106': { code: '0106', name: 'Netherlands VAT', example: 'NL123456789B01' },
  '0190': { code: '0190', name: 'Netherlands KVK', example: '12345678' },
  '9925': { code: '9925', name: 'Luxembourg VAT', example: 'LU12345678' },
  '9930': { code: '9930', name: 'France SIREN/SIRET', example: '123456789' },
  '0088': { code: '0088', name: 'GLN (EAN)', example: '1234567890123' },
  '0096': { code: '0096', name: 'Denmark CVR', example: '12345678' },
  '9906': { code: '9906', name: 'Italy IVA', example: 'IT12345678901' },
  '9914': { code: '9914', name: 'Austria UID', example: 'ATU12345678' },
} as const;

export type PeppolSchemeCode = keyof typeof PEPPOL_SCHEMES;

// Helper function to get scheme info
export function getSchemeInfo(schemeCode: string) {
  return PEPPOL_SCHEMES[schemeCode as PeppolSchemeCode] || { 
    code: schemeCode, 
    name: `Scheme ${schemeCode}`, 
    example: '' 
  };
}

// Helper function to get scheme code from country
export function getSchemeCodeForCountry(country: string): string | null {
  const countrySchemes: Record<string, string> = {
    'Belgium': '0208',
    'Netherlands': '0106',
    'Luxembourg': '9925',
    'France': '9930',
    'Denmark': '0096',
    'Italy': '9906',
    'Austria': '9914',
  };
  return countrySchemes[country] || null;
}

// Peppol document status display helpers
export function getPeppolStatusColor(status: PeppolDocumentStatus): string {
  const colors: Record<PeppolDocumentStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    sending: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    delivered: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    acknowledged: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    received: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    processed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

export function getPeppolStatusLabel(status: PeppolDocumentStatus): string {
  const labels: Record<PeppolDocumentStatus, string> = {
    pending: 'Pending',
    sending: 'Sending',
    sent: 'Sent',
    delivered: 'Delivered',
    acknowledged: 'Acknowledged',
    rejected: 'Rejected',
    failed: 'Failed',
    received: 'Received',
    processed: 'Processed',
  };
  return labels[status] || status;
}
